import { brands } from 'noddi-types';
import type { SMSLoginStepLayout, UserSessionData, VerifiedSignupFormValues } from 'noddi-web';
import { SMSLogin, tracking, useLanguageContext, VerifiedSignupForm } from 'noddi-web';
import { useState, type ReactNode } from 'react';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getErrorCodesWithTrans } from '../../utils/getErrorCodesWithTranslations';

interface CustomerSMSLoginProps {
  onLoginSuccess?: (userData: UserSessionData) => void;
  hidePhoneNumberText?: boolean;
  disablePhoneAutoFocus?: boolean;
  phoneNumberLabel?: string;
  inlineSubmit?: boolean;
  phoneStepFooter?: ReactNode;
  /** Lays out each step as its own page, submit included — see `CheckoutLoginStep`. */
  renderStep?: (step: SMSLoginStepLayout) => ReactNode;
  /** The workspace the flow belongs to — resolves a shared phone to one account. */
  userGroupId?: number;
}
export const CustomerSMSLogin = ({
  onLoginSuccess,
  hidePhoneNumberText,
  disablePhoneAutoFocus,
  phoneNumberLabel,
  inlineSubmit,
  phoneStepFooter,
  renderStep,
  userGroupId
}: CustomerSMSLoginProps) => {
  const [signupDraft, setSignupDraft] = useState<{ phoneNumber: string; values: VerifiedSignupFormValues } | null>(
    null
  );
  const { currentLanguage } = useLanguageContext();
  const { currentBrand, brandEmail } = useCurrentBrand();

  return (
    <SMSLogin
      errorCodes={getErrorCodesWithTrans(brandEmail)}
      translations={getCommonTranslations().smsLoginTranslations}
      language={currentLanguage}
      brandName={currentBrand}
      hidePhoneNumberText={hidePhoneNumberText}
      disablePhoneAutoFocus={disablePhoneAutoFocus}
      phoneNumberLabel={phoneNumberLabel}
      inlineSubmit={inlineSubmit}
      phoneStepFooter={phoneStepFooter}
      renderStep={renderStep}
      userGroupId={userGroupId}
      trackUserIdentity={(user) => {
        tracking.trackUserLogin(user);
      }}
      onLoginSuccess={onLoginSuccess}
      userRegistrationForm={({
        phoneNumber,
        signupChallengeToken,
        onRestartVerification,
        onLoginSuccess: onRegistered
      }) => (
        <VerifiedSignupForm
          onLoginSuccess={onRegistered}
          errorCodes={getErrorCodesWithTrans(brandEmail)}
          translations={getCommonTranslations().registrationTranslations}
          phoneNumber={phoneNumber}
          signupChallengeToken={signupChallengeToken}
          showLicensePlates={currentBrand === brands.tronderdekk}
          initialValues={signupDraft?.phoneNumber === phoneNumber ? signupDraft.values : undefined}
          onRestartVerification={(values) => {
            setSignupDraft({ phoneNumber, values });
            onRestartVerification();
          }}
          trackUserIdentity={(user) => {
            tracking.trackUserIdentity(user);
          }}
        />
      )}
    />
  );
};
